/**
 * Shop Infinite Offers Web Component
 * A Light DOM component that wraps a server-rendered offer grid and its
 * pagination nav, and loads the remaining offers in batches as the member
 * scrolls. Below the desktop breakpoint it loads a few batches on its own,
 * then hands over to a "Load more" button. The nav is hidden while loading
 * works and shown again on any failure, so the numbered pages stay reachable.
 *
 * Attributes:
 * - src: Batch endpoint path, with the frozen-order token already set
 * - total: Number of offers in the frozen order
 * - offset: Index of the first offer not yet rendered
 * - error-message: Announced to screen readers when loading fails
 * - load-more-label: Label of the "Load more" button
 *
 * Example:
 *   <shop-infinite-offers src="/categories/shoes/remaining_offers?token=abc" total="120" offset="24">
 *     <fragno-card-grid>…</fragno-card-grid>
 *     <nav class="fragno-pagination-pages">…</nav>
 *   </shop-infinite-offers>
 */

import { dispatchTrack } from "../../../analytics/shop-analytics-track.js";

const ShopInfiniteOffers = (function () {
  const TAG_NAME = "shop-infinite-offers";
  const BATCH_SIZE = 24;
  const CARD_SELECTOR = "fragno-offer-card, fragno-featured-card";
  // The unbounded top margin counts a last card the viewport jumped past (End key, fling) as reached.
  const ROOT_MARGIN = "1000000px 0px 1200px 0px";
  const STORAGE_PREFIX = "shop-infinite-offers:";
  const DEFAULT_ERROR_MESSAGE =
    "Couldn't load more offers. Use the page links below.";
  const DEFAULT_LOAD_MORE_LABEL = "Load more offers";
  // Mirrors $breakpoint-desktop in reskin/_content-blocks.scss.
  const DESKTOP_QUERY = "(min-width: 1024px)";
  const AUTO_BATCHES_BELOW_DESKTOP = 3;

  function cardKeys(card) {
    try {
      const params = JSON.parse(card.dataset.eventParams || "{}") || {};
      return { offerId: params.offer_id, storeId: params.store_id };
    } catch {
      return {};
    }
  }

  const RESTORED_NAVIGATIONS = ["back_forward", "reload"];

  function isRestoredNavigation() {
    const [entry] = performance.getEntriesByType("navigation");
    return RESTORED_NAVIGATIONS.includes(entry?.type);
  }

  class ShopInfiniteOffers extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      setTimeout(() => this._start(), 0);
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      if (this._handlePageHide) {
        window.removeEventListener("pagehide", this._handlePageHide);
      }
    }

    _start() {
      const src = this.getAttribute("src");
      this._grid = this.querySelector(":scope > fragno-card-grid");
      this._nav = this.querySelector(":scope > nav.fragno-pagination-pages");
      this._total = parseInt(this.getAttribute("total"), 10) || 0;
      this._offset = parseInt(this.getAttribute("offset"), 10) || 0;
      if (!src || !this._grid || this._offset >= this._total) return;

      this._src = new URL(src, window.location.href);
      this._batches = 0;
      this._visibleBatches = 0;
      this._loadMorePresses = 0;
      this._autoBatchLimit = window.matchMedia(DESKTOP_QUERY).matches
        ? Infinity
        : AUTO_BATCHES_BELOW_DESKTOP;
      this._seenOffers = new Set();
      this._seenStores = new Set();

      this._grid.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        this._remember(card);
      });

      if (this._nav) this._nav.hidden = true;
      history.scrollRestoration = "manual";

      this._status = document.createElement("div");
      this._status.classList.add("visually-hidden");
      this._status.setAttribute("role", "status");
      this._status.setAttribute("aria-live", "polite");
      this._status.setAttribute("aria-atomic", "true");
      this.appendChild(this._status);

      this._handlePageHide = () => this._save();
      window.addEventListener("pagehide", this._handlePageHide);

      this._observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this._loadNext();
        },
        { rootMargin: ROOT_MARGIN },
      );

      const saved = isRestoredNavigation() ? this._readSaved() : null;
      if (saved) {
        this._restore(saved);
      } else {
        this._continue();
      }
    }

    async _restore(saved) {
      for (let i = 0; i < saved.batches && !this._done; i++) {
        await this._loadBatch(this._fetch(this._offset));
      }

      window.scrollTo(0, saved.scrollY || 0);
      if (!this._done) this._continue();
    }

    _continue() {
      this._observer.disconnect();
      if (this._visibleBatches < this._autoBatchLimit) {
        this._observer.observe(this._grid.lastElementChild);
      } else {
        this._showLoadMore();
      }
    }

    _showLoadMore() {
      if (this._loadMoreButton) return;

      this._loadMoreButton = document.createElement("fragno-button");
      this._loadMoreButton.classList.add("infinite-offers__load-more");
      this._loadMoreButton.setAttribute("hierarchy", "secondary");
      this._loadMoreButton.setAttribute("size", "md");
      this._loadMoreButton.setAttribute(
        "label",
        this.getAttribute("load-more-label") || DEFAULT_LOAD_MORE_LABEL,
      );
      this._loadMoreButton.addEventListener("click", () => this._loadMore());
      this._grid.after(this._loadMoreButton);
    }

    async _loadMore() {
      if (this._loading) return;
      const firstNew = this._grid.children.length;
      this._loadMoreButton.setAttribute("loading", "");

      this._loadMorePresses += 1;
      dispatchTrack(this._loadMoreButton, "load_more_click", {
        category_slug: decodeURIComponent(this._src.pathname.split("/")[2]),
        category_infinite_scroll_arm: this._grid.dataset.experimentArm,
        absolute_position:
          parseInt(this._grid.dataset.positionOffset, 10) + firstNew + 1,
        press_number: this._loadMorePresses,
      });

      await this._loadNext();
      if (this._failed) return;

      this._loadMoreButton?.removeAttribute("loading");
      const card =
        this._grid.children[firstNew] ||
        (this._done ? this._grid.lastElementChild : null);
      card?.querySelector("a")?.focus();
    }

    async _loadNext() {
      if (this._loading || this._done) return;
      this._loading = true;

      const pending = this._prefetched || this._fetch(this._offset);
      this._prefetched = null;
      await this._loadBatch(pending);
      this._loading = false;

      if (this._done) return;
      this._prefetch();
      this._continue();
    }

    async _loadBatch(pending) {
      this._grid.setAttribute("aria-busy", "true");
      try {
        await this._receiveBatch(pending);
      } finally {
        this._grid.removeAttribute("aria-busy");
      }
    }

    async _receiveBatch(pending) {
      let html;
      try {
        html = await pending;
      } catch {
        this._fail();
        return;
      }

      const { received, added } = this._append(html);
      if (received === 0 && this._offset + BATCH_SIZE < this._total) {
        this._fail();
        return;
      }

      this._offset += BATCH_SIZE;
      this._batches += 1;
      if (added > 0) this._visibleBatches += 1;
      if (this._offset >= this._total) this._finish();
    }

    _prefetch() {
      const pending = this._fetch(this._offset);
      pending.catch(() => {});
      this._prefetched = pending;
    }

    async _fetch(offset) {
      const url = new URL(this._src);
      url.searchParams.set("offset", String(offset));
      const response = await fetch(url, { headers: { Accept: "text/html" } });
      if (response.status !== 200) {
        throw new Error(`${TAG_NAME}: ${response.status}`);
      }
      return response.text();
    }

    _append(html) {
      const template = document.createElement("template");
      template.innerHTML = html;
      const cards = [...template.content.children].filter((card) =>
        card.matches(CARD_SELECTOR),
      );
      const fresh = cards.filter((card) => this._remember(card));
      this._grid.append(...fresh);
      return { received: cards.length, added: fresh.length };
    }

    _remember(card) {
      const { offerId, storeId } = cardKeys(card);
      if (this._seenOffers.has(offerId) || this._seenStores.has(storeId)) {
        return false;
      }
      if (offerId != null) this._seenOffers.add(offerId);
      if (storeId != null) this._seenStores.add(storeId);
      return true;
    }

    _finish() {
      this._done = true;
      this._prefetched = null;
      this._observer.disconnect();
      this._loadMoreButton?.remove();
      this._loadMoreButton = null;
    }

    _fail() {
      const buttonHadFocus = this._loadMoreButton?.contains(
        document.activeElement,
      );
      this._failed = true;
      this._finish();
      if (this._nav) this._nav.hidden = false;
      this._status.textContent =
        this.getAttribute("error-message") || DEFAULT_ERROR_MESSAGE;
      if (buttonHadFocus) this._nav?.querySelector("a")?.focus();
    }

    _storageKey() {
      return STORAGE_PREFIX + window.location.href;
    }

    _save() {
      try {
        sessionStorage.setItem(
          this._storageKey(),
          JSON.stringify({ batches: this._batches, scrollY: window.scrollY }),
        );
      } catch {}
    }

    _readSaved() {
      try {
        return JSON.parse(sessionStorage.getItem(this._storageKey()));
      } catch {
        return null;
      }
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopInfiniteOffers);
  }

  return ShopInfiniteOffers;
})();

export { ShopInfiniteOffers };
